/**
 * The console's session, and the ONE handler for a request that finds it over (console error
 * pages, owner 2026-09-27).
 *
 * Six files used to carry their own handleAdmin401, and they had drifted: four signed the admin
 * out on ANY 401, a step-up refusal included (a mistyped confirmation password cost the session),
 * and all six sent the admin to the sign-in form without a word. Every admin fetch now calls this
 * one; lib/admin/session/__tests__/admin-session.spec.tsx fails if a file keeps a copy.
 *
 * It imports nothing from components/settings/hooks/use-admin.ts (which re-exports the step-up
 * rule from here), so every admin hook file can import it without a cycle.
 */
import { ApiClientError } from '@/lib/api/client'
import { clearAdminUiCookie } from '@/lib/admin/theme/admin-ui-cookie'
import { adminSignedOutHref, currentConsolePath } from './admin-next'
import { leaveFor } from './leave'

/** The admin's bearer token (localStorage). */
export const ADMIN_TOKEN_KEY = 'hf-admin:access-token'
/**
 * The refresh token the 2FA step stores beside it. Nothing reads it today, and a credential left
 * behind after the session ends is still a credential: it goes with the session.
 */
export const ADMIN_REFRESH_KEY = 'hf-admin:refresh-token'
/** The cookie the proxy reads to let a document request into /admin (proxy.ts). */
export const ADMIN_SESSION_COOKIE = 'hf-admin:session'

/**
 * Error codes the backend stamps on a STEP-UP rejection — a re-authentication
 * challenge in front of a privileged action (permanently deleting a company,
 * starting an impersonation session, regenerating recovery codes), NOT the
 * session token being invalid.
 *
 * Kept in lockstep with `SUPER_ADMIN_STEP_UP_ERROR_CODES` in
 * `apps/api/src/auth/super-admin/super-admin-auth.service.ts`
 * (components/settings/hooks/__tests__/step-up-codes-match-api.guard.spec.ts).
 */
export const STEP_UP_ERROR_CODES = {
  /**
   * The challenge itself: the action needs a credential and none was supplied.
   * Added 2026-09-21 alongside the API code. Its absence is what made every
   * impersonation attempt look like an expired session and sign the operator
   * out — the API asked for a code, and the console logged them out instead of
   * showing the field.
   */
  required: 'step_up_required',
  passwordInvalid: 'step_up_password_invalid',
  twoFactorInvalid: 'step_up_two_factor_invalid',
} as const

const STEP_UP_CODE_VALUES = new Set<string>(Object.values(STEP_UP_ERROR_CODES))

/**
 * Is this the step-up challenge — "prove it's you" — rather than a dead session? The positive
 * form of the rule, for a caller that shows the credential field inline.
 */
export function isStepUpError(error: unknown): boolean {
  return error instanceof ApiClientError && error.statusCode === 401 && !!error.errorCode && STEP_UP_CODE_VALUES.has(error.errorCode)
}

/**
 * Whether a failure means "this admin session is over".
 *
 * A 401 answers two different questions on these routes and they must not be
 * conflated: "who are you?" (the bearer token is missing, expired or revoked —
 * the session IS over) and "prove it's you" (a confirmation password or 2FA
 * code was wrong — the session is perfectly valid and the admin is standing
 * right there). Treating the second as the first is what logged an operator out
 * of the super-admin area for mistyping the password in the delete-company
 * dialog, losing the dialog, its context and the work in progress.
 */
export function shouldClearAdminSession(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) return false
  if (error.statusCode !== 401) return false
  return !isStepUpError(error)
}

/**
 * Forget this browser's admin session: the token and the refresh token, the session cookie the
 * proxy reads, and the console theme's mirror (review A7: the next person at this browser starts
 * from Classic until GET admin/me answers with their own choice).
 */
export function clearAdminSession(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(ADMIN_TOKEN_KEY)
  localStorage.removeItem(ADMIN_REFRESH_KEY)
  document.cookie = `${ADMIN_SESSION_COOKIE}=; path=/; max-age=0`
  clearAdminUiCookie()
}

/**
 * Every admin fetch's catch. A 401 that means the session is over clears it and sends the admin to
 * /admin/signed-out, which says so and brings them back to this page after they sign in again. A
 * step-up refusal, and every other failure, is rethrown untouched for the caller to show.
 */
export function handleAdmin401(error: unknown): never {
  if (shouldClearAdminSession(error) && typeof window !== 'undefined') {
    const next = currentConsolePath()
    clearAdminSession()
    leaveFor(adminSignedOutHref(next))
  }
  throw error
}
